import React, { FC, FormEvent, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { TextField, Grid, Paper } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { IError } from 'src/api/types';
import { getRoute } from 'src/routes';
import { PasswordTextField } from 'src/shared/components';
import { useThunkDispatch } from 'src/shared/hooks';
import { getSynerionLogoUrl } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { authenticate } from 'src/store/user/user.actions';
import './LoginPage.styles.scss';

export const LoginPage: FC = () => {
  const theme = useTheme();
  const dispatch = useThunkDispatch();
  const navigate = useNavigate();
  const [subdomain, setSubdomain] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const { authenticated, loading, authenticatedError } = useSelector((state: RootState) => state.user);
  const hasError = showErrors && authenticatedError;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      await dispatch(authenticate({ subdomain, username, password }));
    } catch (e) {
      const error = e as IError;
      const hasError = !!error;

      if (!hasError) {
        return;
      }

      let errorMessage = 'An error occurred. Please try again later.';

      if (+error.status >= 400 && +error.status <= 403) {
        errorMessage = 'Oops! Incorrect subdomain, username and password combination.';
      } else {
        errorMessage = `Oops! ${errorMessage}`;
      }

      toast.error(errorMessage);
      setShowErrors(true);
    }
  };

  useEffect(() => {
    if (!authenticated) {
      return;
    }

    navigate(getRoute('root'));
  }, [authenticated]);

  return (
    <Grid container component="main" className="root">
      <CssBaseline />
      <Paper className="p-4">
        <img alt="synerion-logo" src={getSynerionLogoUrl(theme)} width="260" className="d-block m-auto mb-3" />
        <form className="form" onSubmit={handleSubmit}>
          <TextField
            variant="outlined"
            size="small"
            margin="normal"
            required
            fullWidth
            id="subdomain"
            label="Company Subdomain"
            name="subdomain"
            autoFocus
            value={subdomain}
            error={!!hasError}
            onInput={(e) => {
              setSubdomain((e.target as HTMLInputElement)?.value);
              setShowErrors(false);
            }}
          />
          <TextField
            variant="outlined"
            size="small"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            value={username}
            error={!!hasError}
            onInput={(e) => {
              setUsername((e.target as HTMLInputElement)?.value);
              setShowErrors(false);
            }}
          />
          <PasswordTextField
            variant="outlined"
            size="small"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            id="password"
            autoComplete="current-password"
            value={password}
            error={!!hasError}
            onInput={(e) => {
              setPassword((e.target as HTMLInputElement)?.value);
              setShowErrors(false);
            }}
          />
          <div className="mt-4 d-flex">
            <LoadingButton
              className="w-100"
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              loading={loading}
            >
              Login
            </LoadingButton>
          </div>
        </form>
        <p className="fw-400 small text-center mt-3 mb-0">
          <i>Login page for ESS Development</i>
        </p>
      </Paper>
    </Grid>
  );
};
